/**
 * CardWidget - Container widget with inline editing
 *
 * A card is like a "section" on the screen. It can hold other widgets
 * and has an optional title/subtitle header.
 *
 * When selected, shows:
 * - Inline title/subtitle editing
 * - Variant selector (elevated/outlined/flat)
 * - Voice settings panel
 */

import { memo, useState, useRef, useEffect } from 'react';
import type { CardWidget as CardWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';
import { WIDGET_PADDING } from '../constants';
import { VoiceConfigEditor } from './VoiceConfigEditor';

interface CardWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: CardWidgetType;
}

export const CardWidgetComponent = memo(function CardWidgetComponent({
  widget,
  isSelected,
  onSelect,
  onUpdate,
}: CardWidgetProps) {
  const { props, voice } = widget;
  const variant = props.variant || 'elevated';

  // Local editing state for title/subtitle
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(props.title || '');
  const [editSubtitle, setEditSubtitle] = useState(props.subtitle || '');
  const titleRef = useRef<HTMLInputElement>(null);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleRef.current) {
      titleRef.current.focus();
      titleRef.current.select();
    }
  }, [isEditingTitle]);

  // Sync local state with props
  useEffect(() => {
    setEditTitle(props.title || '');
    setEditSubtitle(props.subtitle || '');
  }, [props.title, props.subtitle]);

  // Double-click to edit title inline
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingTitle(true);
  };

  // Save title/subtitle changes
  const handleSaveTitle = () => {
    setIsEditingTitle(false);
    if (!onUpdate) return;
    if (editTitle !== props.title || editSubtitle !== props.subtitle) {
      onUpdate({
        ...widget,
        props: {
          ...widget.props,
          title: editTitle || 'Card Title',
          subtitle: editSubtitle || undefined,
        },
      });
    }
  };

  // Handle keyboard in title editor
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle();
    }
    if (e.key === 'Escape') {
      setEditTitle(props.title || '');
      setEditSubtitle(props.subtitle || '');
      setIsEditingTitle(false);
    }
  };

  // Handle variant change
  const handleVariantChange = (newVariant: string) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, variant: newVariant as CardWidgetType['props']['variant'] },
    });
  };

  // Handle voice config change
  const handleVoiceChange = (voiceConfig: typeof voice) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      voice: voiceConfig,
    });
  };

  return (
    <div
      className={`
        relative rounded-xl transition-all duration-200 cursor-pointer
        ${variant === 'elevated' ? 'bg-white shadow-md' : ''}
        ${variant === 'outlined' ? 'bg-white border-2 border-gray-200' : ''}
        ${variant === 'flat' ? 'bg-gray-50' : ''}
        ${isSelected ? 'ring-2 ring-violet-500 ring-offset-2' : ''}
      `}
      style={{ padding: WIDGET_PADDING }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Card header - inline editable */}
      {isEditingTitle ? (
        <div className="mb-2 space-y-1" onClick={(e) => e.stopPropagation()}>
          <input
            ref={titleRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={handleKeyDown}
            placeholder="Card Title"
            className="nodrag nopan w-full px-2 py-1 text-base font-semibold text-gray-900 border border-violet-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
          />
          <input
            type="text"
            value={editSubtitle}
            onChange={(e) => setEditSubtitle(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyDown={handleKeyDown}
            placeholder="Subtitle (optional)"
            className="nodrag nopan w-full px-2 py-1 text-sm text-gray-500 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
          />
        </div>
      ) : (
        <>
          {props.title && (
            <div className="mb-2" title="Double-click to edit">
              <h3 className="text-base font-semibold text-gray-900">{props.title}</h3>
              {props.subtitle && (
                <p className="text-sm text-gray-500 mt-0.5">{props.subtitle}</p>
              )}
            </div>
          )}
        </>
      )}

      {/* Placeholder for nested content */}
      <div className="min-h-[40px] flex items-center justify-center text-gray-400 text-sm">
        {widget.children?.length ? (
          <span>Contains {widget.children.length} widget(s)</span>
        ) : (
          <span className="italic">Empty card</span>
        )}
      </div>

      {/* Voice indicator badge (shown when NOT selected) */}
      {!isSelected && voice?.speakable && (
        <div
          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center"
          title={voice.alias ? `Voice: "${voice.alias}"` : 'Voice-enabled'}
        >
          <svg
            className="w-3 h-3 text-violet-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
          </svg>
        </div>
      )}

      {/* ─── INLINE EDITOR (shown when selected and NOT title-editing) ─── */}
      {isSelected && !isEditingTitle && (
        <div
          className="mt-3 pt-3 border-t border-gray-200 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Variant selector */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-gray-500">Variant:</span>
            {(['elevated', 'outlined', 'flat'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleVariantChange(v)}
                className={`
                  nodrag nopan px-2 py-0.5 text-[10px] rounded border transition-colors
                  ${variant === v
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-violet-200'
                  }
                `}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          {/* Voice settings (expandable) */}
          <VoiceConfigEditor config={voice} onChange={handleVoiceChange} />

          <p className="text-[9px] text-gray-400">
            Double-click to edit title/subtitle
          </p>
        </div>
      )}
    </div>
  );
});
